import { useCallback, useEffect, useRef } from "react";
import api from "../services/api";

const DEFAULT_DELAY = 800;

/**
 * Debounced task PATCHes for a list view, batched per task.
 *
 * The list views shared a single `useDebounce` timer across every row and every
 * field. lodash's debounce keeps only the most recent call, so editing one task
 * and then another inside the window silently discarded the first edit: the
 * optimistic update made it look saved, and it reverted on the next refresh.
 *
 * Each task gets its own timer here, and repeated edits to the same task merge
 * into one request instead of replacing each other.
 *
 * @param {Object} params
 * @param {number} params.workspaceId
 * @param {(error: any) => void} [params.onError] Called once per failed task.
 * @param {number} [params.delay]
 * @returns {(taskId: number, updates: Object) => void}
 */
export default function useTaskListPatcher({
  workspaceId,
  onError,
  delay = DEFAULT_DELAY,
}) {
  // taskId -> { timer, updates }
  const pendingRef = useRef(new Map());
  const cfgRef = useRef({});
  cfgRef.current = { workspaceId, onError };

  const flush = useCallback(async (taskId) => {
    const entry = pendingRef.current.get(taskId);
    if (!entry) return;
    pendingRef.current.delete(taskId);
    clearTimeout(entry.timer);

    const { updates } = entry;
    if (Object.keys(updates).length === 0) return;

    try {
      await api.patch(`tasks/${taskId}/`, {
        ...updates,
        workspace: cfgRef.current.workspaceId,
      });
    } catch (error) {
      console.error("Error updating task:", error, { taskId, updates });
      cfgRef.current.onError?.(error);
    }
  }, []);

  const patchTask = useCallback(
    (taskId, updates) => {
      if (!taskId || !updates) return;
      const existing = pendingRef.current.get(taskId);
      if (existing) clearTimeout(existing.timer);
      pendingRef.current.set(taskId, {
        updates: { ...(existing?.updates || {}), ...updates },
        timer: setTimeout(() => flush(taskId), delay),
      });
    },
    [flush, delay]
  );

  // Send anything still queued when the list unmounts (navigating away mid-edit).
  useEffect(
    () => () => {
      [...pendingRef.current.keys()].forEach((taskId) => flush(taskId));
    },
    [flush]
  );

  return patchTask;
}
